import { Component, OnDestroy, OnInit, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import {
  animate,
  style,
  transition,
  trigger,
  keyframes,
} from '@angular/animations';

import { Subscription } from 'rxjs';
import {
  ActivityType,
  ZERO_IMPACT_ACTIVITIES,
} from 'src/app/trip-calculator/models/activity';
import { BrochureAnalysisService } from 'src/app/trip-calculator/services/brochure-analysis.service';
import { AiErrorTranslatorService } from 'src/app/shared/services/ai-error-translator.service';
import { AiErrorResponse } from 'src/app/shared/models/ai-error-response';
import { GreentripperLoaderComponent } from 'src/app/shared/components/greentripper-loader/greentripper-loader.component';
import { LucideAngularModule } from 'lucide-angular';
import { Transport } from '../../models/transport';

@Component({
  selector: 'app-brochure-analysis-modal',
  standalone: true,
  imports: [
    CommonModule,
    ProgressSpinnerModule,
    DialogModule,
    ButtonModule,
    TranslocoModule,
    TagModule,
    DividerModule,
    GreentripperLoaderComponent,
    LucideAngularModule,
  ],
  templateUrl: './brochure-analysis-modal.component.html',
  styleUrls: ['./brochure-analysis-modal.component.scss'],
  animations: [
    trigger('fadeMessage', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('400ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('400ms ease-out', style({ opacity: 0 }))]),
      transition('* => *', [
        animate('400ms ease-out', style({ opacity: 0 })),
        animate('400ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
    trigger('successAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate(
          '600ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
    ]),
    trigger('checkmarkAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0)' }),
        animate(
          '500ms 300ms ease-out',
          keyframes([
            style({ opacity: 0.5, transform: 'scale(1.2)', offset: 0.7 }),
            style({ opacity: 1, transform: 'scale(1)', offset: 1 }),
          ]),
        ),
      ]),
    ]),
  ],
})
export class BrochureAnalysisModalComponent implements OnInit, OnDestroy {
  private translocoService = inject(TranslocoService);
  brochureAnalysisService = inject(BrochureAnalysisService);
  private aiErrorTranslator = inject(AiErrorTranslatorService);

  private subscriptions = new Subscription();

  // Messages à afficher pendant le chargement
  loadingMessages: string[] = [
    'Notre IA est en train de lire votre brochure ligne par ligne...',
    'Détection des destinations et des activités...',
    'Analyse des transports et des hébergements...',
    'Extraction des informations sur les repas...',
    "Finalisation de l'analyse...",
  ];
  currentMessageIndex = 0;
  private messageInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    effect(() => {
      if (this.brochureAnalysisService.state()?.status === 'loading') {
        this.startMessageRotation();
      } else {
        this.stopMessageRotation();
      }
    });
  }

  // Getters pour simplifier les vérifications de nullité dans le template
  get hasTransports(): boolean {
    return (
      (this.brochureAnalysisService.state().result?.transports?.length ?? 0) > 0
    );
  }

  get hasAccommodations(): boolean {
    return (
      (this.brochureAnalysisService.state().result?.accommodations?.length ??
        0) > 0
    );
  }

  get hasActivities(): boolean {
    return (
      (this.brochureAnalysisService.state().result?.activities?.length ?? 0) > 0
    );
  }

  get hasMeals(): boolean {
    return (
      (this.brochureAnalysisService.state().result?.meals?.length ?? 0) > 0
    );
  }

  get hasWaste(): boolean {
    return !!this.brochureAnalysisService.state()?.result?.wasteType;
  }

  // Méthode pour vérifier si une activité est à impact zéro
  isZeroImpactActivity(activityType: string): boolean {
    return ZERO_IMPACT_ACTIVITIES.includes(activityType as ActivityType);
  }

  // Méthodes d'aide pour les accès aux tableaux
  getFlightStopAirportCode(transport: Transport, index: number): string {
    if (!transport.flightStops || !transport.flightStops[index]) {
      return '-';
    }
    return transport.flightStops[index].airportCode || '-';
  }

  getRoadStopAddress(transport: Transport, index: number): string {
    if (!transport.roadStops || !transport.roadStops[index]) {
      return '-';
    }
    return transport.roadStops[index].address || '-';
  }

  /** Destination is always the last road stop, regardless of how many waypoints exist. */
  getDestinationRoadStopAddress(transport: Transport): string {
    const roadStops = transport.roadStops;
    if (!roadStops || roadStops.length === 0) return '-';
    return roadStops[roadStops.length - 1].address || '-';
  }

  ngOnInit(): void {
    // Rotation des messages pendant le chargement
    this.startMessageRotation();

    // Charger les traductions pour les messages de chargement
    this.initializeLoadingMessages();
  }

  private initializeLoadingMessages(): void {
    const keys = [
      'calculator.aiAnalysisLoading.readingDocument',
      'calculator.aiAnalysisLoading.detectingDestinations',
      'calculator.aiAnalysisLoading.analyzingTransports',
      'calculator.aiAnalysisLoading.extractingMeals',
      'calculator.aiAnalysisLoading.finalizing',
    ];

    // Utiliser les valeurs par défaut en attendant les traductions
    this.loadingMessages = [
      'Notre IA est en train de lire votre brochure ligne par ligne...',
      'Détection des destinations et des activités...',
      'Analyse des transports et des hébergements...',
      'Extraction des informations sur les repas...',
      "Finalisation de l'analyse...",
    ];

    // S'abonner aux traductions
    keys.forEach((key, index) => {
      const sub = this.translocoService.selectTranslate(key).subscribe({
        next: (translation) => {
          if (translation && translation !== key) {
            this.loadingMessages[index] = translation;
          }
        },
        error: (error) => {
          console.error(`Erreur de traduction pour la clé ${key}:`, error);
        },
      });
      this.subscriptions.add(sub);
    });
  }

  ngOnDestroy(): void {
    this.stopMessageRotation();
    this.subscriptions.unsubscribe();
  }

  private startMessageRotation(): void {
    this.stopMessageRotation();
    this.currentMessageIndex = 0;
    this.messageInterval = setInterval(() => {
      // Utiliser une animation de fondu enchaîné pour changer de message
      this.currentMessageIndex =
        (this.currentMessageIndex + 1) % this.loadingMessages.length;
    }, 4000);
  }

  private stopMessageRotation(): void {
    if (this.messageInterval) {
      clearInterval(this.messageInterval);
      this.messageInterval = null;
    }
  }

  onHide(): void {
    this.brochureAnalysisService.showBrochureAnalysisModal.set(false);
  }

  getTransportTypeLabel(type: string): string {
    return this.translocoService.translate(`categoryType.${type}`);
  }

  getAccommodationTypeLabel(type: string): string {
    return this.translocoService.translate(`categoryType.${type}`);
  }

  getActivityTypeLabel(type: string): string {
    return this.translocoService.translate(`categoryType.${type}`);
  }

  getMealTypeLabel(type: string): string {
    return this.translocoService.translate(`categoryType.${type}`);
  }

  getWasteTypeLabel(type: string): string {
    return this.translocoService.translate(`categoryType.${type}`);
  }

  /**
   * Get translated error message
   * @returns Object with message and optional suggestions
   */
  getTranslatedError(): { message: string; suggestions?: string[] } {
    const error = this.brochureAnalysisService.state().error;

    if (!error) {
      return {
        message: this.translocoService.translate(
          'calculator.aiError.generic.message',
        ),
      };
    }

    // If error is a string, return it as-is
    if (typeof error === 'string') {
      return { message: error };
    }

    // If error is a structured AiErrorResponse, translate it
    return this.aiErrorTranslator.translateError(error as AiErrorResponse);
  }
}
